import { useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribe } from "@/lib/api";

export const GetInvolved = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [zip, setZip] = useState("");
  const [interest, setInterest] = useState("volunteer");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribe({
        email,
        name: name || undefined,
        zip_code: zip || undefined,
        interests: [interest],
      });
      toast.success("You're on the team. Welcome to Together We Grow.");
      setEmail("");
      setName("");
      setZip("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const interests = [
    { id: "volunteer", label: "Volunteer" },
    { id: "host_event", label: "Host an Event" },
    { id: "yard_sign", label: "Request a Yard Sign" },
    { id: "general", label: "Stay Informed" },
  ];

  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="relative bg-royal py-24 lg:py-32 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-10">
        <div className="border border-white/15 bg-royal-deep/60 backdrop-blur p-8 sm:p-12 lg:p-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-lavender/10 border border-lavender/40 mb-6">
                <Mail className="text-lavender" size={26} strokeWidth={1.5} />
              </div>
              <p className="text-[11px] tracking-[0.32em] uppercase text-lavender mb-4">
                Be part of the growth
              </p>
              <h2
                data-testid="involved-heading"
                className="font-serif-display text-classy text-4xl sm:text-5xl lg:text-6xl leading-[0.95] font-medium mb-5"
              >
                Join our <span className="italic font-light">team!</span>
              </h2>
              <p className="text-classy-soft/85 text-[15px] leading-relaxed">
                Sign up to volunteer, stay informed, and help us build a
                stronger Ward 3. Every neighbor matters.
              </p>
            </div>

            <form
              onSubmit={submit}
              data-testid="signup-form"
              className="lg:col-span-7 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name"
                  className="w-full bg-transparent border border-white/25 focus:border-classy outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors"
                />
                <input
                  type="text"
                  placeholder="Zip code"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  data-testid="signup-zip"
                  className="w-full bg-transparent border border-white/25 focus:border-classy outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors"
                />
              </div>

              <input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email"
                className="w-full bg-transparent border border-white/25 focus:border-classy outline-none text-classy placeholder-classy-soft/50 px-5 py-4 text-sm rounded-sm transition-colors"
              />

              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-classy-soft mb-3">
                  How do you want to help?
                </p>
                <div className="flex flex-wrap gap-2">
                  {interests.map((i) => (
                    <button
                      type="button"
                      key={i.id}
                      onClick={() => setInterest(i.id)}
                      data-testid={`signup-interest-${i.id}`}
                      className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 rounded-full border transition-all duration-300 ${
                        interest === i.id
                          ? "bg-lavender text-royal-deep border-lavender"
                          : "border-white/30 text-classy-soft hover:border-lavender hover:text-lavender"
                      }`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="signup-submit"
                className="btn-classy w-full sm:w-auto px-10 py-4 rounded-full text-xs uppercase inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Signing up
                  </>
                ) : (
                  "Sign Up"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GetInvolved;
